
'use client';

import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { useFriend } from '@/context/FriendContext';
import type { Friend } from '@/types';
import Link from 'next/link';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Search } from 'lucide-react';
import { Skeleton } from './ui/skeleton';

interface Suggestion {
    friend: Friend;
    match: string; // e.g., "3D Sculptor" or the friend's name
}

export function SearchArtists() {
    const { friends, loading } = useFriend();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (query.length > 0) {
            const lowerCaseQuery = query.toLowerCase();
            const newSuggestions: Suggestion[] = [];
            
            friends.forEach(friend => {
                // Check name match
                if (friend.name.toLowerCase().includes(lowerCaseQuery)) {
                    newSuggestions.push({ friend, match: friend.name });
                    return; // Avoid adding the same friend twice if a tag also matches
                }

                // Check tag matches
                friend.tags.forEach(tag => {
                    if (tag.toLowerCase().includes(lowerCaseQuery)) {
                         // Check if this friend is already added to avoid duplicates
                        if (!newSuggestions.some(s => s.friend.id === friend.id)) {
                             newSuggestions.push({ friend, match: tag });
                        }
                    }
                });
            });
            setSuggestions(newSuggestions.slice(0, 10)); // Limit to 10 suggestions
        } else {
            setSuggestions([]);
        }
    }, [query, friends]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchRef]);


    return (
        <div className="relative hidden md:block" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
                placeholder="Search for artists..." 
                className="pl-10 h-10 w-[250px] bg-secondary rounded-full"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
            />
            {isFocused && (query.length > 0 || loading) && (
                <div className="absolute top-12 left-0 w-[300px] bg-card border rounded-lg shadow-lg z-50">
                    <ul className="py-2">
                        {loading ? (
                             <li className="px-4 py-2 text-sm text-muted-foreground flex items-center gap-2">
                                <Skeleton className="h-4 w-4" /> Loading...
                             </li>
                        ) : suggestions.length > 0 ? (
                            suggestions.map(({ friend, match }) => (
                                <li key={friend.id}>
                                    <Link href={`/friends/${friend.id}`} className="flex items-center gap-3 px-4 py-2 hover:bg-secondary" onClick={() => setIsFocused(false)}>
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src={friend.avatarUrl} />
                                            <AvatarFallback>{friend.name.substring(0, 2)}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-semibold text-sm">{friend.name}</p>
                                            {match !== friend.name && (
                                                <p className="text-xs text-muted-foreground">via: <span className="italic">{match}</span></p>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            ))
                        ) : (
                            <li className="px-4 py-2 text-sm text-muted-foreground">No artists found.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
